import { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getRedirectLinkToTag } from '@karma/utils/functions/redirectLink';
import Typography from '@karma/components/Typography';
import Price from 'components/atoms/TagCardPrice/TagCardPrice';
import ItemConditionMarker from 'components/atoms/ItemConditionMarker/ItemConditionMarker';
import Alooma from 'services/alooma';
import * as ALOOMA from 'consts/alooma';
import { getProductModalFeature } from 'store/features/selectors';
import { ReferralSourceContext } from 'contexts/ReferralSourceContext';
import useAction from 'hooks/useAction';
import { pushBackProductModal } from 'store/modals/actions';
import { ENTITIES } from 'consts/entities';
import { DEFAULT_TAG } from 'consts/defaultImages';
import useTagIsOutOfStock from 'hooks/useTagIsOutOfStock';
import TagRegularImageOverlay from 'components/molecules/TagRegularImageOverlay/TagRegularImageOverlay';
import TagEditableImageOverlay from '../TagEditableImageOverlay/TagEditableImageOverlay';
import { ImageWrapper, Wrapper, ContentWrapper, HeightController, StyledTagImage } from './CardTagRegular.style';

const CardTagRegular = ({ group, tag, isEditable, displayCheckbox, clickable, isObjectFitContain }) => {
  const contextDataAttrs = Alooma.useContextDataAttrs();
  const productModalFeature = useSelector(getProductModalFeature);
  const pushBackProductModalFunc = useAction(pushBackProductModal);
  const source = useContext(ReferralSourceContext);
  const isOutOfStock = useTagIsOutOfStock(tag.marker);

  const {
    main_img_url: mainImgUrl,
    name,
    brand,
    price,
    stake,
    id,
    rule_name: ruleName,
    user_id: userId,
    item_images: itemImages = [],
    original_url: originalUrl,
    retailer_name: retailerName,
  } = tag || {};
  const retailerLogoSrc = tag.retailer_logo || tag.logo_image_uri;
  const mainSrc = mainImgUrl || itemImages?.[0] || retailerLogoSrc || DEFAULT_TAG;

  const handleClick = async () => {
    if (!clickable) return;

    if (productModalFeature) {
      pushBackProductModalFunc({ group, tagId: tag.id });
    } else {
      window.open(
        getRedirectLinkToTag({
          tagId: id,
          ruleName,
          userId,
          singleShare: true,
          cashback: !!stake,
          source,
          originalUrl,
        }),
        '_blank',
      );
    }
  };

  const formatItemName = () => {
    if (!name) return null;
    if (name?.includes(brand) || !brand || ['null', 'undefined', 'NaN'].includes(brand)) return name;

    return `${brand} ${name}`;
  };

  return (
    <Wrapper
      {...contextDataAttrs}
      data-alooma-element={ALOOMA.ELEMENTS.TAG}
      onClick={handleClick}
      direction="column"
      justifyContent="flex-start"
      role="button"
    >
      <ImageWrapper>
        <HeightController />
        <StyledTagImage
          mainSrc={mainSrc}
          backupSrc={retailerLogoSrc}
          objectFit={isObjectFitContain ? 'contain' : undefined}
        />
        {isEditable ? (
          <TagEditableImageOverlay tag={tag} displayCheckbox={displayCheckbox} />
        ) : (
          <TagRegularImageOverlay tag={tag} />
        )}
      </ImageWrapper>

      <ContentWrapper direction="column" spacing={2} justifyContent="flex-start" alignItems="flex-start" fullWidth>
        <Price
          currentPrice={price.current_price}
          originalPrice={price.original_price}
          currency={price.currency.symbol}
          isOutOfStock={isOutOfStock}
          v2
        />
        <Typography variant="labelSmall" bMargin={4} noWrap className="card-tag-name">
          {formatItemName()}
        </Typography>
        <Typography variant="labelExtraSmallUppercase" color="neutral7" noWrap className="card-tag-retailer">
          {retailerName}
        </Typography>
        {!isOutOfStock && <ItemConditionMarker itemCondition={tag.item_condition} />}
      </ContentWrapper>
    </Wrapper>
  );
};

CardTagRegular.propTypes = {
  group: ENTITIES.tagGroup,
  tag: ENTITIES.tag.isRequired,
  clickable: PropTypes.bool,
  isEditable: PropTypes.bool,
  displayCheckbox: PropTypes.bool,
  isObjectFitContain: PropTypes.bool,
};

CardTagRegular.defaultProps = {
  group: null,
  isEditable: false,
  clickable: true,
  displayCheckbox: false,
  isObjectFitContain: false,
};

export default CardTagRegular;
